/**
 * המרת מבנה deal / linkedFlights מ-SearchContext (תוצאות submitSearch)
 * למבנה יציב ל-FlightBookingFlowMockup + שמירת rawDeal לצורך פירוט.
 *
 * מסלול (route): נגזר מקטעי flight ב-linkedFlights — קטע ראשון = הלוך,
 * לא מכל הלגים המשוטחים (שב-RT מסתיים בלג חזור ל-TLV ויוצר TLV→TLV שגוי).
 */

import { AGENT_FEATURE_KEYS } from "../../../features/agentEntitlements/constants";
import { resolveFlightHrCommissionAmount } from "../../../pricing/flightHrCommission";

/** מניעת הצפה — אזהרת legacy HR shadow פעם אחת לכל dealId בסשן המודול */
const legacyHrShadowWarnedDealIds = new Set();
const LEGACY_HR_SHADOW_WARN_CAP = 120;

function getLinkedFlightsRoot(deal) {
  return deal?.linkedFlights ?? deal?.linkedflights ?? null;
}

function asArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function toSegmentNode(value) {
  if (!value) return null;
  if (value?.leg) return value;
  return { leg: value };
}

/** שורת SSOT ל־popover מחיר טיסה — לפי מפתח פיצ'ר מהמנוע, לא טקסט קשיח */
export function resolveFlightHrMarginSsotUiLine({
  sourceFeatureKey,
  unifiedHrMarginPercent,
}) {
  const fk = String(sourceFeatureKey || "").trim();
  const K = AGENT_FEATURE_KEYS;
  if (fk === K.FLIGHT_HR_COMMISSION_PERCENTAGE) {
    return "SSOT: package_features.flight_hr_commission_percentage";
  }
  if (fk === K.HOTEL_HR_COMMISSION_PERCENTAGE) {
    return "SSOT fallback: package_features.hotel_hr_commission_percentage";
  }
  if (fk === K.VACATION_PACKAGE_HR_COMMISSION_PERCENTAGE) {
    return "SSOT: package_features.vacation_package_hr_commission_percentage";
  }
  const pct = Number(unifiedHrMarginPercent);
  if (Number.isFinite(pct) && pct > 0 && !fk) {
    return "SSOT fallback: global_constants / legacy HR margin";
  }
  return "SSOT: unknown — check pricing audit";
}

function getFlightSegments(deal) {
  const lf = getLinkedFlightsRoot(deal);
  const fromLinkedFlights = asArray(lf?.flight).filter(Boolean);
  if (fromLinkedFlights.length > 0) return fromLinkedFlights;

  const flightNode = deal?.flight ?? null;
  if (!flightNode) return [];

  if (Array.isArray(flightNode)) {
    return flightNode.map(toSegmentNode).filter(Boolean);
  }
  if (Array.isArray(flightNode?.segments)) {
    return flightNode.segments.map(toSegmentNode).filter(Boolean);
  }
  if (Array.isArray(flightNode?.segment)) {
    return flightNode.segment.map(toSegmentNode).filter(Boolean);
  }

  const outbound = toSegmentNode(flightNode?.outbound);
  const inbound = toSegmentNode(flightNode?.return);
  if (outbound || inbound) {
    return [outbound, inbound].filter(Boolean);
  }

  const single = toSegmentNode(flightNode);
  return single ? [single] : [];
}

function legsFromSegment(seg) {
  const leg = seg?.leg;
  if (!leg) return [];
  return Array.isArray(leg) ? leg : [leg];
}

/** קטעים עם מערכי לגים (סדר LDS: קטע 0 = הלוך, קטע 1 = חזור בדרך כלל) */
export function getFlightSegmentsWithLegs(deal) {
  return getFlightSegments(deal)
    .map((segment) => ({
      segment,
      legs: legsFromSegment(segment).filter(Boolean),
    }))
    .filter((s) => s.legs.length > 0);
}

/**
 * לאיזה אינדקסי קטע (0..n-1) מתייחס supplement לפי fltRef (LDS).
 * -3 או ריק ב-RT: כל הקטעים (בחירה נפרדת לכל כיוון במסך המוקאב).
 */
export function resolveSupplementTargetSegmentIndexes(fltRefRaw, numSegments) {
  const n = Math.max(1, Math.floor(Number(numSegments)) || 1);
  if (n === 1) return [0];

  const ref =
    fltRefRaw !== undefined && fltRefRaw !== null
      ? String(fltRefRaw).trim()
      : "";

  if (ref === "" || ref === "-3") {
    return Array.from({ length: n }, (_, idx) => idx);
  }
  if (ref === "-2" || ref === "-1" || ref === "0") return [0];
  if (ref === "1") return [1];
  const parsed = Number.parseInt(ref, 10);
  if (Number.isFinite(parsed) && parsed >= 0 && parsed < n) return [parsed];
  if (Number.isFinite(parsed) && parsed >= n) return [n - 1];
  return [0];
}

/** תווית כיוון לתצוגת extras / סיכום (null ב-OW) */
export function formatExtrasSegmentDirectionLabel(segmentIndex, numSegments) {
  const n = Math.max(1, Math.floor(Number(numSegments)) || 1);
  if (n <= 1) return null;
  const s = Math.max(0, Math.floor(Number(segmentIndex)) || 0);
  if (s === 0) return "הלוך";
  if (s === 1) return "חזור";
  return `קטע ${s + 1}`;
}

function legEndpointCode(leg, end) {
  if (!leg) return "?";
  if (end === "dep") {
    return leg.depAirport?.code || leg.depCity || "?";
  }
  return leg.arrAirport?.code || leg.arrCity || "?";
}

function buildRouteLabelFromSegments(deal) {
  const segLegs = getFlightSegmentsWithLegs(deal);
  if (segLegs.length === 0) return null;

  const out = segLegs[0].legs;
  const dep = legEndpointCode(out[0], "dep");
  const primaryDest = legEndpointCode(out[out.length - 1], "arr");

  if (segLegs.length >= 2) {
    return `${dep} ↔ ${primaryDest}`;
  }
  return `${dep} → ${primaryDest}`;
}

/** כל הלגים לפי סדר (הלוך/חזור וכו') */
export function flattenLegsFromDeal(deal) {
  const out = [];
  for (const seg of getFlightSegments(deal)) {
    for (const L of legsFromSegment(seg)) {
      if (L) out.push(L);
    }
  }
  return out;
}

export function buildRouteLabel(deal) {
  const fromSeg = buildRouteLabelFromSegments(deal);
  if (fromSeg) return fromSeg;

  const legs = flattenLegsFromDeal(deal);
  if (legs.length === 0) return "—";
  const dep = legEndpointCode(legs[0], "dep");
  const arr = legEndpointCode(legs[legs.length - 1], "arr");
  return `${dep} → ${arr}`;
}

function formatTime(dateStr) {
  if (dateStr == null || dateStr === "") return "";
  const s = typeof dateStr === "string" ? dateStr.trim() : String(dateStr).trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const afterT = s.slice(s.indexOf("T") + 1);
    return afterT.replace(/Z$/i, "").split(".")[0] || "";
  }
  const parts = s.split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1] : s;
}

/** זמן קצר לתצוגת כרטיס (למשל 14:20) מתוך מחרוזת LDS / ISO */
export function shortClockFromLdsDateTime(dateStr) {
  if (dateStr == null || dateStr === "") return "";
  const t = formatTime(dateStr);
  if (!t) return "";
  const m = t.match(/(\d{1,2}):(\d{2})/);
  if (!m) return "";
  const h = String(parseInt(m[1], 10)).padStart(2, "0");
  const min = m[2].padStart(2, "0");
  return `${h}:${min}`;
}

function formatDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return "";
  const parts = dateStr.split(" ");
  return parts[0] || "";
}

function formatDurationMinutes(min) {
  const n = Number(min);
  if (!isFinite(n) || n <= 0) return "—";
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${h}:${m < 10 ? `0${m}` : m}`;
}

function formatLegBulletLine(leg, dirLabel) {
  const dep = legEndpointCode(leg, "dep");
  const arr = legEndpointCode(leg, "arr");
  const tDep = formatTime(leg?.depDate);
  const tArr = formatTime(leg?.arrDate);
  const dDep = formatDate(leg?.depDate);
  const fn = leg?.flightNumber || "";
  const dur = formatDurationMinutes(leg?.duration);
  return `${dirLabel}: ${dep} ${tDep} (${dDep}) → ${arr} ${tArr} · ${fn || "—"} · ${dur}`;
}

function positiveFareNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** dealFare ב-root או תחת fullDeal (כמו ב-SearchContext) — בלי שינוי מבנה האובייקט */
function resolveFlightDealFare(deal) {
  if (deal?.dealFare && typeof deal.dealFare === "object") return deal.dealFare;
  if (
    deal?.fullDeal?.dealFare &&
    typeof deal.fullDeal.dealFare === "object"
  ) {
    return deal.fullDeal.dealFare;
  }
  return null;
}

function resolveFlightFareDetails(deal) {
  if (deal?.fareDetails && typeof deal.fareDetails === "object") {
    return deal.fareDetails;
  }
  if (
    deal?.fullDeal?.fareDetails &&
    typeof deal.fullDeal.fareDetails === "object"
  ) {
    return deal.fullDeal.fareDetails;
  }
  return null;
}

function fareField(dealFare, camel, pascal) {
  if (!dealFare) return undefined;
  const a = dealFare[camel];
  if (a !== undefined && a !== null && a !== "") return a;
  return dealFare[pascal];
}

function fareDetailsCurrency(fareDetails) {
  if (!fareDetails) return "";
  const c = fareDetails.currency ?? fareDetails.Currency;
  return c != null ? String(c).trim() : "";
}

/** ספירת נוסעים מהרכב חדרים בחיפוש (לשורת משנה בסיכום תוצאות) */
export function countTotalPaxFromRoomInfo(roomInfo) {
  const rooms = roomInfo?.rooms;
  if (!Array.isArray(rooms) || rooms.length === 0) return null;
  let n = 0;
  for (const r of rooms) {
    n += Math.max(0, Math.floor(Number(r?.adults) || 0));
    n += Math.max(0, Math.floor(Number(r?.kids) || 0));
    n += Math.max(0, Math.floor(Number(r?.infants) || 0));
  }
  return n > 0 ? n : null;
}

/**
 * תצוגת מחיר לכרטיס תוצאה: adultFare מהספק (SSOT) כשקיים; אחרת totalFare / מספר נוסעים.
 * @param {object} deal
 * @param {{ totalPaxCount?: number | null }} [options]
 */
export function buildFlightOfferPricingDisplay(deal, options = {}) {
  const fareDetails = resolveFlightFareDetails(deal);
  const currency = fareDetailsCurrency(fareDetails);
  const df = resolveFlightDealFare(deal);

  const totalFare = positiveFareNumber(fareField(df, "totalFare", "TotalFare"));
  const adultFareRaw = fareField(df, "adultFare", "AdultFare");
  const adultFare = positiveFareNumber(adultFareRaw);
  const childFare = positiveFareNumber(
    fareField(df, "childFare", "ChildFare") ??
      fareField(df, "childFareExt", "ChildFareExt")
  );
  const infantFare = positiveFareNumber(
    fareField(df, "infantFare", "InfantFare")
  );

  const fmt = (amount, cur) =>
    cur ? `${amount} ${cur}` : String(amount);

  const totalFareDisplayLabel =
    totalFare != null ? fmt(totalFare, currency) : "—";

  const totalPaxCount = options.totalPaxCount;
  const computedPerPerson =
    totalFare != null && totalPaxCount != null && totalPaxCount > 0
      ? totalFare / totalPaxCount
      : null;

  let pricePerPerson = adultFare;
  if (adultFare == null && computedPerPerson != null) {
    pricePerPerson = computedPerPerson;
  }

  const pricePerPersonDisplayLabel =
    pricePerPerson != null
      ? `${fmt(pricePerPerson, currency)} לאדם`
      : totalFare != null
        ? totalFareDisplayLabel
        : "—";

  let resultsTotalFareSubline = null;
  if (pricePerPerson != null && totalFare != null && currency) {
    if (totalPaxCount === 1 && totalFare !== pricePerPerson) {
      resultsTotalFareSubline = `סה״כ ${fmt(totalFare, currency)}`;
    } else if (
      (totalPaxCount == null || totalPaxCount <= 0) &&
      totalFare !== pricePerPerson
    ) {
      resultsTotalFareSubline = `סה״כ ${fmt(totalFare, currency)}`;
    }
  }

  const displayedPrice =
    pricePerPerson != null ? pricePerPerson : totalFare ?? null;

  return {
    rawCurrency: currency || null,
    totalFare,
    totalFareDisplayLabel,
    pricePerPerson,
    pricePerPersonDisplayLabel,
    resultsTotalFareSubline,
    childFare,
    infantFare,
    displayedPrice,
  };
}

function safePositiveNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * When originalCurrency is ILS, calculateLDSDealFareForRoomsComposition may still fill
 * origCurrency.priceWithCommissionAndCreditCardFee with the USD party total (ILS total ÷ fx).
 * buildFlightClientPriceDisplay used to require originalCurrency === "USD", which hid those
 * valid finals. We accept origCurrency USD total only when it matches aggregated ILS ÷ fx
 * (same relationship as SearchContext — not API gross / trace fallbacks).
 */
function isOrigCurrencyUsdTotalAlignedWithAggregatedIls(ourPrice, origUsdTotal) {
  const ils = safePositiveNumber(ourPrice?.ourTotalFare?.priceWithCommissionAndCreditCardFee);
  const fx = Number(ourPrice?.ourTotalFare?.fx_rate);
  if (ils == null || !Number.isFinite(fx) || fx <= 0 || origUsdTotal == null) {
    return false;
  }
  const impliedUsdTotal = ils / fx;
  const diff = Math.abs(origUsdTotal - impliedUsdTotal);
  const tol = Math.max(0.5, 0.0001 * Math.max(impliedUsdTotal, origUsdTotal));
  return diff <= tol;
}

function formatIlsMoney(amount) {
  const n = safePositiveNumber(amount);
  if (n == null) return null;
  const formatted = new Intl.NumberFormat("he-IL", {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  }).format(n);
  return `₪ ${formatted}`;
}

export function buildFlightClientPriceDisplay({
  ourPrice,
  totalPaxCount,
  rawTotalFare,  // ← הוספנו את הפרמטר!
  rawCurrency,   // ← הוספנו את הפרמטר!
}) {
  const pax = Number(totalPaxCount);
  const passengersCount = Number.isFinite(pax) && pax > 0 ? pax : 1;
  const originalCurrency = String(
    ourPrice?.ourTotalFare?.originalCurrency || 
    rawCurrency || 
    ""
  )
    .trim()
    .toUpperCase();
  
  const originalTotal = safePositiveNumber(
    ourPrice?.ourTotalFare?.origCurrency?.priceWithCommissionAndCreditCardFee ||
    ourPrice?.ourTotalFare?.totalFare ||
    rawTotalFare
  );
  
  // אם אין ourPrice והשתמשנו ב-rawTotalFare, צריך להוסיף עמלת HR ידנית!
  const isRawTotalWithoutHr = 
    !ourPrice?.ourTotalFare?.origCurrency?.priceWithCommissionAndCreditCardFee &&
    !ourPrice?.ourTotalFare?.totalFare &&
    originalTotal != null &&
    originalTotal > 0;
  
  if (isRawTotalWithoutHr && process.env.NODE_ENV === "development") {
    console.warn("[FLIGHT_PRICE_USING_RAW_WITHOUT_HR]", {
      rawTotalFare,
      originalTotal,
      message: "Using rawTotalFare without HR commission - price may be incorrect"
    });
  }
  
  // DEBUG: תמיד הדפס כדי לראות מה קורה
  if (process.env.NODE_ENV === "development") {
    console.log("[CURRENCY_DETECTION]", {
      step: "before_logic",
      originalCurrency,
      originalTotal,
      rawCurrency,
      rawTotalFare,
      passengersCount,
      ourPriceExists: !!ourPrice,
      origCurrencyFieldExists: !!ourPrice?.ourTotalFare?.origCurrency,
    });
  }
  
  // תמיכה ב-USD, EUR ומטבעות אחרים - נשתמש במחיר המקורי
  const useOrigCurrencyAsFinalUsdTotal =
    originalTotal != null &&
    originalTotal > 0 &&  // ← חדש! וודא שהסכום חיובי
    originalCurrency !== "" &&  // ← חדש! וודא שיש מטבע
    (originalCurrency === "USD" ||
      originalCurrency === "EUR" ||
      originalCurrency === "GBP" ||
      (originalCurrency === "ILS" &&
        isOrigCurrencyUsdTotalAlignedWithAggregatedIls(ourPrice, originalTotal)));
  
  const baseUsdPerPerson = useOrigCurrencyAsFinalUsdTotal
    ? originalTotal / passengersCount
    : null;
  const finalClientUsdPerPerson = baseUsdPerPerson;
  
  // DEBUG: תמיד הדפס את התוצאה
  if (process.env.NODE_ENV === "development") {
    console.log("[CURRENCY_DETECTION]", {
      step: "after_calculation",
      useOrigCurrencyAsFinalUsdTotal,
      baseUsdPerPerson,
      finalClientUsdPerPerson,
    });
  }
  
  const agentFeeUsdPerPerson =
    Number(ourPrice?.ourTotalFare?.origCurrency?.agentCommission) > 0
      ? Number(ourPrice.ourTotalFare.origCurrency.agentCommission) / passengersCount
      : Number(ourPrice?.ourTotalFare?.agentCommission) > 0 &&
          Number(ourPrice?.ourTotalFare?.fx_rate) > 0
        ? Number(ourPrice.ourTotalFare.agentCommission) /
          Number(ourPrice.ourTotalFare.fx_rate) /
          passengersCount
        : 0;
  
  // displayedUsd הוא שם היסטורי - יכול להכיל EUR, GBP או USD!
  // הערך האמיתי הוא במטבע המקורי (originalCurrency)
  const displayedUsd =
    finalClientUsdPerPerson != null
      ? Math.max(0, finalClientUsdPerPerson - agentFeeUsdPerPerson)
      : null;
  
  if (process.env.NODE_ENV === "development" && displayedUsd == null) {
    console.warn("[FLIGHT_DISPLAY_PRICE_MISSING_FINAL_USD]", {
      expectedField: "ourTotalFare.origCurrency.priceWithCommissionAndCreditCardFee",
      originalCurrency: originalCurrency || null,
      originalTotal: originalTotal ?? null,
    });
  }
  
  // DEBUG
  if (typeof window !== "undefined" && window.__HR_DEBUG_CURRENCY) {
    console.log("[buildFlightClientPriceDisplay]", {
      originalCurrency,
      originalTotal,
      displayedUsd,
      passengersCount,
      rawTotalFare,
      rawCurrency,
      sources: {
        origCurrency: ourPrice?.ourTotalFare?.origCurrency?.priceWithCommissionAndCreditCardFee,
        totalFare: ourPrice?.ourTotalFare?.totalFare,
        rawTotalFareParam: rawTotalFare,
      }
    });
  }
  const fxRate =
    Number(ourPrice?.ourTotalFare?.fx_rate) > 0
      ? Number(ourPrice?.ourTotalFare?.fx_rate)
      : null;
  const usdRateUsed =
    displayedUsd != null && fxRate != null ? fxRate : null;
  const displayedIls =
    displayedUsd != null && fxRate != null ? displayedUsd * fxRate : null;
  const totalIls =
    displayedIls != null ? displayedIls * passengersCount : null;
  if (displayedUsd == null) {
    return {
      totalIls: null,
      perPersonIls: null,
      perPersonUsd: null,
      originalCurrency: originalCurrency || null,  // ← חדש! חשוב!
      perPersonUsdLabel: "price unavailable",
      totalIlsLabel: null,
      perPersonIlsLabel: null,
      sourceApproxLabel: null,
      usdRateUsed: null,
    };
  }
  const sourceApproxLabel = null;
  if (process.env.NODE_ENV === "development") {
    const usdRateRaw = usdRateUsed;
    const baseIlsPerPerson =
      baseUsdPerPerson != null && usdRateRaw != null
        ? baseUsdPerPerson * usdRateRaw
        : null;
    const creditCardFee = (Number(ourPrice?.ourTotalFare?.creditCardFee) || 0) / passengersCount;
    const systemFee = (Number(ourPrice?.ourTotalFare?.handlingFee) || 0) / passengersCount;
    const agentMarkup = (Number(ourPrice?.ourTotalFare?.agentCommission) || 0) / passengersCount;
    const hrMarkup = (Number(ourPrice?.ourTotalFare?.hrCommission) || 0) / passengersCount;
    const finalIlsPerPerson = displayedIls;
    console.log("[FLIGHT_PRICE_BREAKDOWN_DEBUG]", {
      baseUsdPerPerson,
      usdRate: usdRateRaw,
      baseIlsPerPerson,
      finalClientUsdPerPerson,
      agentFeeUsdPerPerson,
      creditCardFee,
      systemFee,
      agentMarkup,
      hrMarkup,
      finalIlsPerPerson,
      passengersCount,
      rawTotalIls: totalIls,
    });
    console.log("[FLIGHT_CURRENCY_MODE_DEBUG]", {
      baseUsdPerPerson,
      finalClientUsdPerPerson,
      agentFeeUsdPerPerson,
      displayedUsd,
      displayedIls,
      usdRateUsed: usdRateRaw,
    });
  }
  return {
    totalIls,
    perPersonIls: displayedIls,
    perPersonUsd: displayedUsd,
    originalCurrency: originalCurrency || null,  // ← חדש! שמור את המטבע המקורי
    finalClientUsdPerPerson,
    agentFeeUsdPerPerson,
    perPersonUsdLabel:
      displayedUsd != null ? `$${Math.round(displayedUsd)} per person` : "price unavailable",
    totalIlsLabel: formatIlsMoney(totalIls),
    perPersonIlsLabel:
      displayedIls != null ? `${formatIlsMoney(displayedIls)} לאדם` : null,
    sourceApproxLabel,
    usdRateUsed,
  };
}

export function buildFlightPriceTraceForUi({
  deal,
  ourPrice,
  totalPaxCount,
  clientPricing,
}) {
  const pickFirstFiniteOptional = (...candidates) => {
    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined || candidate === "") continue;
      const value = Number(candidate);
      if (Number.isFinite(value)) return value;
    }
    return null;
  };
  const pickFirstNonNegativeNumber = (...candidates) => {
    for (const candidate of candidates) {
      const value = Number(candidate);
      if (Number.isFinite(value) && value >= 0) {
        return value;
      }
    }
    return 0;
  };
  const paxRaw = Number(totalPaxCount);
  const passengersCount = Number.isFinite(paxRaw) && paxRaw > 0 ? paxRaw : 1;
  const apiGrossUsdTotal = safePositiveNumber(deal?.dealFare?.totalFare) || 0;
  const apiGrossUsdPerPerson = apiGrossUsdTotal / passengersCount;
  const commissionRaw = deal?.fareDetails?.commission || {};
  const supplierCommissionType = String(
    commissionRaw?.commissionType || commissionRaw?.type || "PERCENT"
  )
    .trim()
    .toUpperCase();
  const supplierCommissionValue = Number(
    commissionRaw?.commissionValue ?? commissionRaw?.value ?? 0
  );
  const normalizedSupplierCommissionValue =
    Number.isFinite(supplierCommissionValue) && supplierCommissionValue > 0
      ? supplierCommissionValue
      : 0;
  const supplierCommissionAmountUsd =
    supplierCommissionType === "PERCENT"
      ? (apiGrossUsdTotal * normalizedSupplierCommissionValue) / 100
      : 0;
  const supplierCommissionAmountUsdPerPerson =
    supplierCommissionAmountUsd / passengersCount;
  const netUsdAfterSupplierCommissionPerPerson = Math.max(
    0,
    apiGrossUsdPerPerson - supplierCommissionAmountUsdPerPerson
  );
  const origCurrencyVm =
    ourPrice?.ourTotalFare?.origCurrency &&
    typeof ourPrice.ourTotalFare.origCurrency === "object"
      ? ourPrice.ourTotalFare.origCurrency
      : null;
  const usdRate = Number(ourPrice?.ourTotalFare?.fx_rate) || null;
  const toUsdPerPersonFromIls = (ilsVal) => {
    const n = Number(ilsVal);
    if (!Number.isFinite(n) || n <= 0 || !usdRate || usdRate <= 0) return 0;
    return n / usdRate / passengersCount;
  };
  const legacyHrShadowAmountUsdPerPerson =
    Number(origCurrencyVm?.hrCommission) > 0
      ? Number(origCurrencyVm.hrCommission) / passengersCount
      : toUsdPerPersonFromIls(ourPrice?.ourTotalFare?.hrCommission);
  /** סדר תואם מנוע: אחוז HR מאוחד / audit לפני wallet commission_percentage (לא מקור מרווח מערכת בטיסות סוכן) */
  const configuredSystemMarginPercent = pickFirstNonNegativeNumber(
    ourPrice?.ourTotalFare?.finalHrPercentUsed,
    ourPrice?.finalHrPercentUsed,
    origCurrencyVm?.finalHrPercentUsed,
    ourPrice?.ourTotalFare?.unifiedHrMarginPercent,
    ourPrice?.unifiedHrMarginPercent,
    origCurrencyVm?.unifiedHrMarginPercent,
    origCurrencyVm?.agentCommissionPercent,
    ourPrice?.ourTotalFare?.agentCommissionPercent,
    ourPrice?.ourTotalFare?.packageFeatureCommissionPercentage,
    ourPrice?.packageFeatureCommissionPercentage,
    origCurrencyVm?.packageFeatureCommissionPercentage
  );
  const configuredSystemMarginAmountUsdPerPerson = resolveFlightHrCommissionAmount({
    percentAmount:
      (netUsdAfterSupplierCommissionPerPerson * configuredSystemMarginPercent) / 100,
    passengersCount: 1,
  });
  const resolvedPackageCode =
    (ourPrice?.ourTotalFare?.resolvedPackageCode &&
      String(ourPrice.ourTotalFare.resolvedPackageCode).trim()) ||
    (ourPrice?.resolvedPackageCode && String(ourPrice.resolvedPackageCode).trim()) ||
    null;
  const agentMarkupAmountUsdPerPerson =
    Number(origCurrencyVm?.agentCommission) > 0
      ? Number(origCurrencyVm.agentCommission) / passengersCount
      : toUsdPerPersonFromIls(ourPrice?.ourTotalFare?.agentCommission);
  const creditCardFeeUsdPerPerson =
    Number(origCurrencyVm?.creditCardFee) > 0
      ? Number(origCurrencyVm.creditCardFee) / passengersCount
      : toUsdPerPersonFromIls(ourPrice?.ourTotalFare?.creditCardFee);
  const agentNetUsdPerPerson = Number(clientPricing?.perPersonUsd) || 0;
  const finalClientUsdPerPerson =
    Number(clientPricing?.finalClientUsdPerPerson) > 0
      ? Number(clientPricing.finalClientUsdPerPerson)
      : agentNetUsdPerPerson;
  const displayedUsdPerPerson = finalClientUsdPerPerson;
  const usdRateUsed =
    Number(clientPricing?.usdRateUsed) > 0 ? Number(clientPricing.usdRateUsed) : null;
  const ilsHelper = usdRateUsed != null ? agentNetUsdPerPerson * usdRateUsed : null;
  const baseWithoutRoundingPerPerson =
    netUsdAfterSupplierCommissionPerPerson +
    configuredSystemMarginAmountUsdPerPerson +
    agentMarkupAmountUsdPerPerson +
    creditCardFeeUsdPerPerson;
  const roundingDeltaUsd = finalClientUsdPerPerson - baseWithoutRoundingPerPerson;
  const deltaFromApiGrossUsd = displayedUsdPerPerson - apiGrossUsdPerPerson;
  const sourceCandidates = [
    { key: "system_margin", amount: configuredSystemMarginAmountUsdPerPerson },
    { key: "credit_card", amount: creditCardFeeUsdPerPerson },
    { key: "agent_markup", amount: agentMarkupAmountUsdPerPerson },
    { key: "rounding", amount: roundingDeltaUsd },
    { key: "supplier_commission", amount: -supplierCommissionAmountUsdPerPerson },
  ];
  const primaryDeltaSource = sourceCandidates
    .slice()
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))[0]?.key;
  const traceSourceKey =
    String(
      ourPrice?.ourTotalFare?.unifiedHrMarginSourceFeatureKey ||
        ourPrice?.unifiedHrMarginSourceFeatureKey ||
        origCurrencyVm?.unifiedHrMarginSourceFeatureKey ||
        ""
    ).trim() || null;

  const unifiedHrMarginPercentForSsotLabel = pickFirstNonNegativeNumber(
    ourPrice?.ourTotalFare?.unifiedHrMarginPercent,
    ourPrice?.unifiedHrMarginPercent,
    origCurrencyVm?.unifiedHrMarginPercent
  );
  const hrMarginSsotUiLine = resolveFlightHrMarginSsotUiLine({
    sourceFeatureKey: traceSourceKey,
    unifiedHrMarginPercent: unifiedHrMarginPercentForSsotLabel,
  });

  const traceFinalHrPercentUsed = pickFirstFiniteOptional(
    ourPrice?.ourTotalFare?.finalHrPercentUsed,
    ourPrice?.finalHrPercentUsed,
    origCurrencyVm?.finalHrPercentUsed
  );
  const traceUnifiedHrMarginPercent = pickFirstFiniteOptional(
    ourPrice?.ourTotalFare?.unifiedHrMarginPercent,
    ourPrice?.unifiedHrMarginPercent,
    origCurrencyVm?.unifiedHrMarginPercent
  );
  const tracePackageFeatureCommissionPercentage = pickFirstFiniteOptional(
    ourPrice?.ourTotalFare?.packageFeatureCommissionPercentage,
    ourPrice?.packageFeatureCommissionPercentage,
    origCurrencyVm?.packageFeatureCommissionPercentage
  );

  const legacyShadowDealKey = String(
    deal?.dealId ?? deal?.ID ?? deal?.id ?? "__unknown__"
  );
  if (
    process.env.NODE_ENV === "development" &&
    legacyHrShadowAmountUsdPerPerson > 0 &&
    !legacyHrShadowWarnedDealIds.has(legacyShadowDealKey)
  ) {
    if (legacyHrShadowWarnedDealIds.size >= LEGACY_HR_SHADOW_WARN_CAP) {
      legacyHrShadowWarnedDealIds.clear();
    }
    legacyHrShadowWarnedDealIds.add(legacyShadowDealKey);
    console.debug("[FLIGHT_LEGACY_HR_SHADOW_DEPRECATED]", {
      dealId: legacyShadowDealKey,
      legacyHrShadowAmountUsd: legacyHrShadowAmountUsdPerPerson,
      configuredSystemMarginRate: configuredSystemMarginPercent,
      configuredSystemMarginAmountUsd: configuredSystemMarginAmountUsdPerPerson,
      note: "Legacy HR shadow is deprecated and excluded from Flight Price Trace. (once per dealId in DEV)",
    });
  }
  return {
    apiGrossUsdPerPerson,
    supplierCommissionAmountUsd: supplierCommissionAmountUsdPerPerson,
    netUsdAfterSupplierCommission: netUsdAfterSupplierCommissionPerPerson,
    configuredSystemMarginRate: configuredSystemMarginPercent,
    systemMarginAmountUsd: configuredSystemMarginAmountUsdPerPerson,
    systemMarginPercent: configuredSystemMarginPercent,
    hrCommissionAmountUsd: configuredSystemMarginAmountUsdPerPerson,
    hrCommissionPercent: configuredSystemMarginPercent,
    resolvedPackageCode,
    agentMarkupAmountUsd: agentMarkupAmountUsdPerPerson,
    creditCardFeeUsd: creditCardFeeUsdPerPerson,
    roundingDeltaUsd,
    finalClientUsdPerPerson: agentNetUsdPerPerson,
    displayedUsdPerPerson,
    usdRateUsed,
    ilsHelper,
    deltaFromApiGrossUsd,
    primaryDeltaSource: primaryDeltaSource || "unknown",
    hrMarginSsotUiLine,
    hrMarginTraceSourceFeatureKey: traceSourceKey,
    /** שדות אבחון לכרטיס — לא משפיעים על תמחור */
    finalHrPercentUsed: traceFinalHrPercentUsed,
    unifiedHrMarginPercent: traceUnifiedHrMarginPercent,
    packageFeatureCommissionPercentage: tracePackageFeatureCommissionPercentage,
    configuredSystemMarginPercent,
  };
}

/** מחיר בסיס מהספק בלבד — ללא המרות וללא עמלות (סה״כ להרכב — ל-Extras / סיידבר) */
export function getRawLdsDealFareDisplay(deal) {
  const df = resolveFlightDealFare(deal);
  const totalFare = Number(
    fareField(df, "totalFare", "TotalFare") ?? NaN
  );
  const currency = fareDetailsCurrency(resolveFlightFareDetails(deal));
  const hasFare = Number.isFinite(totalFare) && totalFare > 0;
  const priceDisplayLabel =
    hasFare && currency
      ? `${totalFare} ${currency}`
      : hasFare
        ? String(totalFare)
        : "—";
  return {
    rawTotalFare: hasFare ? totalFare : null,
    rawCurrency: currency || null,
    priceDisplayLabel,
  };
}

/** LDS לעיתים מחזיר params כאובייקט יחיד */
function normalizeLdsSupplementParams(params) {
  if (params == null) return [];
  if (Array.isArray(params)) return params;
  if (typeof params === "object" && params.name != null) return [params];
  return [];
}

/**
 * מפתח יציב לבחירות כבודה במוקאב — חייב להיות זהה ל־mapRawLdsSupplementsToMockupExtras ולשמירת LDS.
 */
export function computeMockupSupplementKey(item, segIdx, itemLoopIndex) {
  const type = item?.type || item?.Type;
  const params = normalizeLdsSupplementParams(item.params);
  const w = params.find((p) => p.name === "weight")?.value;
  const u = params.find((p) => p.name === "unit")?.value ?? "";
  const weightPart =
    w != null && String(w) !== "" ? `${String(w).toUpperCase()}${u || ""}` : "";
  const fareRaw = item.fare != null ? String(item.fare).trim() : "";
  return `sk_s${segIdx}_${type}_${String(
    item.id ?? item["@seqNum"] ?? itemLoopIndex
  )}_${fareRaw}_${weightPart || "nw"}_${itemLoopIndex}`;
}

/**
 * שורות תצוגה למוקאב טיסות — מקור האמת: rawDeal.supplement (fare גולמי, לא ourFare).
 * category=PERFLIGHTANDPAX → טקסט "לנוסע לכיוון" בשדה הנלווה.
 */
export function mapRawLdsSupplementsToMockupExtras(rawDeal) {
  let supplements = rawDeal?.supplement;
  if (supplements == null) return [];
  if (typeof supplements === "object" && !Array.isArray(supplements)) {
    supplements = [supplements];
  }
  const segLegs = getFlightSegmentsWithLegs(rawDeal);
  const numSegments = segLegs.length > 0 ? segLegs.length : 1;
  const currency = String(rawDeal?.fareDetails?.currency ?? "").trim();
  const rows = [];
  let i = 0;
  for (const item of supplements) {
    const type = item?.type || item?.Type;
    if (type !== "TROLLEY" && type !== "LUGGAGE" && type !== "BAGGAGE") {
      continue;
    }
    const params = normalizeLdsSupplementParams(item.params);
    const w = params.find((p) => p.name === "weight")?.value;
    const u = params.find((p) => p.name === "unit")?.value ?? "";
    const weightPart =
      w != null && String(w) !== "" ? `${String(w).toUpperCase()}${u || ""}` : "";
    const desc = (item.desc || item.description || "").trim();
    const fareRaw = item.fare != null ? String(item.fare).trim() : "";
    const fareAmount = Number.parseFloat(String(fareRaw).replace(/,/g, ""));
    const fareNumeric = Number.isFinite(fareAmount) ? fareAmount : 0;
    const category = item.category || "";
    const isTrolley = type === "TROLLEY";
    const kindHe = isTrolley ? "טרולי" : "מזוודה";
    const titleCore = desc || kindHe;
    const withWeight = weightPart ? `${titleCore} ${weightPart}` : titleCore;
    const segIndexes = resolveSupplementTargetSegmentIndexes(
      item.fltRef,
      numSegments
    );
    for (const segIdx of segIndexes) {
      const supplementKey = computeMockupSupplementKey(item, segIdx, i);
      rows.push({
        key: `${type}-${item.id ?? item["@seqNum"] ?? i}-s${segIdx}`,
        supplementKey,
        segmentIndex: segIdx,
        segmentKey: `seg-${segIdx}`,
        type,
        titleLine: withWeight,
        description: desc,
        weightLabel: weightPart,
        fare: fareRaw,
        fareAmount: fareNumeric,
        currency,
        perPaxDirectionHe:
          category === "PERFLIGHTANDPAX" ? "לנוסע לכיוון" : null,
        fltRef: item.fltRef,
        category,
        allowsInfant: isTrolley,
      });
    }
    i += 1;
  }
  return rows;
}

/**
 * רשימת נוסעים לתצוגת בחירת כבודה — לפי roomInfo מהחיפוש (ללא "חדרים" בכותרת).
 * @returns {{ id: string, labelHe: string, role: "adult"|"child"|"infant" }[]}
 */
export function buildPaxListFromRoomInfo(roomInfo) {
  const rooms = Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : [];
  if (rooms.length === 0) {
    return [{ id: "r0_adult_0", labelHe: "נוסע 1 – מבוגר", role: "adult" }];
  }
  const list = [];
  let adultN = 0;
  let childN = 0;
  let infantN = 0;
  for (let ri = 0; ri < rooms.length; ri++) {
    const r = rooms[ri];
    const adults = Math.max(0, Math.floor(Number(r?.adults) || 0));
    for (let j = 0; j < adults; j++) {
      adultN += 1;
      list.push({
        id: `r${ri}_adult_${j}`,
        labelHe: `נוסע ${adultN} – מבוגר`,
        role: "adult",
      });
    }
    const kids = Math.max(0, Math.floor(Number(r?.kids) || 0));
    for (let j = 0; j < kids; j++) {
      childN += 1;
      list.push({
        id: `r${ri}_child_${j}`,
        labelHe: `ילד ${childN}`,
        role: "child",
      });
    }
    const inf = Math.max(0, Math.floor(Number(r?.infants) || 0));
    for (let j = 0; j < inf; j++) {
      infantN += 1;
      list.push({
        id: `r${ri}_infant_${j}`,
        labelHe: `תינוק ${infantN}`,
        role: "infant",
      });
    }
  }
  return list.length > 0
    ? list
    : [{ id: "r0_adult_0", labelHe: "נוסע 1 – מבוגר", role: "adult" }];
}

/**
 * מיזוג רשימת נוסעים לכבודה (ids קבועים כמו buildPaxListFromRoomInfo) עם שמות מטופס הנוסעים.
 * סדר האינדקסים תואם ל-PassengersStepCard: לכל חדר — מבוגרים, ילדים, תינוקות.
 *
 * @param {{ roomInfo?: object, passengers?: object[] }} args
 * @returns {Array<{ id: string, role: "adult"|"child"|"infant", labelHe: string, label: string, fallbackLabel: string }>}
 */
export function mapPaxListWithPassengerNames({ roomInfo, passengers }) {
  const base = buildPaxListFromRoomInfo(roomInfo);
  const paxArr = Array.isArray(passengers) ? passengers : [];
  return base.map((slot, i) => {
    const fallbackLabel = slot.labelHe;
    const p = paxArr[i];
    const fn = String(p?.firstName ?? "").trim();
    const ln = String(p?.lastName ?? "").trim();
    const hasBoth = fn.length > 0 && ln.length > 0;
    const label = hasBoth ? `${fn} ${ln}`.trim() : fallbackLabel;
    return {
      ...slot,
      fallbackLabel,
      label,
      labelHe: label,
    };
  });
}

/**
 * סכום כבודה client-side לפי בחירות פר-נוסע × מחיר יחידה (מ-supplement.fare).
 * @param {Record<string, string[]>} selections
 * @param {Record<string, number>} unitFares
 */
export function computeSupplementSelectionExtrasTotal(selections, unitFares) {
  if (!selections || typeof selections !== "object") return 0;
  let sum = 0;
  for (const [key, ids] of Object.entries(selections)) {
    const n = Array.isArray(ids) ? ids.length : 0;
    const unit = Number(unitFares?.[key]);
    if (n > 0 && Number.isFinite(unit)) sum += n * unit;
  }
  return sum;
}

/**
 * שורות סיכום לביקורת — מבוסס על בחירות המוקאב (extras) + מיפוי קטעים.
 */
export function buildMockupExtrasSelectionSummaryLines(extras, rawDeal) {
  const selections = extras?.supplementPaxSelections;
  if (!selections || typeof selections !== "object") return [];
  const rows = mapRawLdsSupplementsToMockupExtras(rawDeal);
  const byKey = new Map(rows.map((r) => [r.supplementKey, r]));
  const numSeg = getFlightSegmentsWithLegs(rawDeal).length || 1;
  const lines = [];
  for (const [key, ids] of Object.entries(selections)) {
    if (!Array.isArray(ids) || ids.length === 0) continue;
    const row = byKey.get(key);
    if (!row) continue;
    const dir = formatExtrasSegmentDirectionLabel(row.segmentIndex, numSeg);
    const unit = Number(extras?.supplementUnitFares?.[key]);
    const n = ids.length;
    let pricePart = "";
    if (Number.isFinite(unit) && unit > 0) {
      pricePart = row.currency
        ? ` · +${unit * n} ${row.currency}`
        : ` · +${unit * n}`;
    }
    const prefix = dir ? `${dir}: ` : "";
    lines.push(`${prefix}${row.titleLine} · ${n} נוסעים${pricePart}`);
  }
  return lines;
}

/** סכום fare גולמי (supplement.fare) לפריטי כבודה מסומנים — מ-orig.supp כפי שמאתחל initLdsSupplements */
export function sumCheckedRawSupplementFares(ldsSupplement) {
  if (!ldsSupplement || typeof ldsSupplement !== "object") return 0;
  return Object.values(ldsSupplement).reduce((acc, entry) => {
    if (!entry?.checked) return acc;
    const f = Number(entry?.orig?.supp?.fare);
    return acc + (Number.isFinite(f) ? f : 0);
  }, 0);
}

/**
 * @param {object} deal - אובייקט deal מ-availableFlights
 * @param {number} index
 * @param {object} [roomInfo] - מ-SearchContext (לספירת נוסעים לשורת סה״כ)
 * @returns {object} אובייקט תואם selectedFlightOffer + שדות תצוגה (מחיר גולמי מ-dealFare / fareDetails)
 */
export function adaptDealToFlightOffer(deal, index, roomInfo, options = {}) {
  const id = String(
    deal?.dealId ?? deal?.ID ?? deal?.id ?? `search-deal-${index}`
  );
  const route = buildRouteLabel(deal);
  const totalPaxCount = countTotalPaxFromRoomInfo(roomInfo);
  const pricing = buildFlightOfferPricingDisplay(deal, { totalPaxCount });
  const { rawTotalFare } = getRawLdsDealFareDisplay(deal);
  const priceUsd = rawTotalFare != null ? rawTotalFare : 0;
  const clientPricing = buildFlightClientPriceDisplay({
    ourPrice: options?.ourPrice,
    totalPaxCount,
    rawTotalFare: pricing.totalFare,
    rawCurrency: pricing.rawCurrency,
  });
  const priceTrace = buildFlightPriceTraceForUi({
    deal,
    ourPrice: options?.ourPrice,
    totalPaxCount,
    clientPricing,
  });

  const airlineIds = [
    ...new Set(
      flattenLegsFromDeal(deal)
        .map((l) => l?.airlineId || l?.airlineCode)
        .filter(Boolean)
    ),
  ];
  const airlineSummary = airlineIds.length ? airlineIds.join(", ") : "—";
  const cardMeta = extractFlightCardMetaFromDeal(deal);
  const cardLegRows = buildFlightCardLegRowsFromDeal(deal);

  return {
    id,
    route,
    priceUsd,
    /** @deprecated להעדיף pricePerPersonDisplayLabel — נשאר לתאימות: מחיר לאדם או סה״כ אם אין adultFare */
    priceDisplayLabel: pricing.pricePerPersonDisplayLabel,
    rawTotalFare,
    rawCurrency: pricing.rawCurrency,
    originalCurrency: clientPricing.originalCurrency || pricing.rawCurrency || null,  // ← חדש!
    totalFare: pricing.totalFare,
    totalFareDisplayLabel: pricing.totalFareDisplayLabel,
    pricePerPerson: pricing.pricePerPerson,
    pricePerPersonDisplayLabel: pricing.pricePerPersonDisplayLabel,
    priceClientTotalIls: clientPricing.totalIls,
    priceClientPerPersonIls: clientPricing.perPersonIls,
    priceClientPerPersonUsd: clientPricing.perPersonUsd,
    priceClientPerPersonUsdLabel: clientPricing.perPersonUsdLabel,
    priceClientTotalLabel: clientPricing.totalIlsLabel,
    priceClientPerPersonLabel: clientPricing.perPersonIlsLabel,
    priceClientSourceApproxLabel: clientPricing.sourceApproxLabel,
    priceTrace,
    resultsTotalFareSubline: pricing.resultsTotalFareSubline,
    totalPaxCount,
    airlineSummary,
    cardMeta,
    cardLegRows,
    rawDeal: deal,
    source: "searchContext",
  };
}

export function normalizeAvailableFlights(availableFlights) {
  if (availableFlights == null) return [];
  return Array.isArray(availableFlights)
    ? availableFlights
    : [availableFlights];
}

/**
 * מטא־דאטה תפעולית לכרטיס תוצאה — משדות ה־deal כפי שחוזרים מ־LDS (linkedFlights, supplierDetails).
 * קטע ראשון בלבד: יציאה מלג ראשון, נחיתה מלג אחרון באותו קטע (חיבורים).
 */
export function extractFlightCardMetaFromDeal(deal) {
  const segLegs = getFlightSegmentsWithLegs(deal);
  const supplierCode =
    deal?.supplierDetails?.supplierId ??
    deal?.supplierDetails?.SupplierId ??
    deal?.supplierdetails?.supplierId ??
    null;

  if (!segLegs.length) {
    const empty = {
      departureTime: "",
      arrivalTime: "",
      timesLabel: "—",
      flightCategory: null,
      airlineCode: null,
      supplierCode: supplierCode != null ? String(supplierCode) : null,
      flightNumber: null,
      duration: null,
      depDate: null,
      arrDate: null,
      legCount: 0,
      isDirect: true,
      originCode: null,
      destinationCode: null,
      depDateDisplay: "",
    };
    if (process.env.NODE_ENV !== "test") {
      console.log("Flight card meta", {
        depDate: empty.depDate,
        arrDate: empty.arrDate,
        flightCategory: empty.flightCategory,
        airlineCode: empty.airlineCode,
        supplierCode: empty.supplierCode,
        flightNumber: empty.flightNumber,
      });
    }
    return empty;
  }

  const legs = segLegs[0].legs;
  const legCount = legs.length;
  const isDirect = legCount <= 1;
  const firstLeg = legs[0];
  const lastLeg = legs[legs.length - 1];
  const depDate = firstLeg?.depDate ?? null;
  const arrDate = lastLeg?.arrDate ?? null;
  const departureTime = depDate ? shortClockFromLdsDateTime(depDate) : "";
  const arrivalTime = arrDate ? shortClockFromLdsDateTime(arrDate) : "";
  let timesLabel = "—";
  if (departureTime && arrivalTime) {
    timesLabel = `${departureTime} → ${arrivalTime}`;
  } else if (departureTime) {
    timesLabel = departureTime;
  } else if (arrivalTime) {
    timesLabel = arrivalTime;
  }

  const flightCategory = firstLeg?.flightCategory ?? null;
  const airlineCode =
    firstLeg?.airlineId != null && String(firstLeg.airlineId).trim() !== ""
      ? String(firstLeg.airlineId).trim()
      : firstLeg?.airlineCode != null &&
          String(firstLeg.airlineCode).trim() !== ""
        ? String(firstLeg.airlineCode).trim()
        : null;

  let durationMinutes = 0;
  for (const L of legs) {
    const d = Number(L?.duration);
    if (Number.isFinite(d) && d > 0) durationMinutes += d;
  }
  const durationFormatted =
    durationMinutes > 0
      ? formatDurationMinutes(durationMinutes)
      : firstLeg?.duration != null
        ? formatDurationMinutes(Number(firstLeg.duration))
        : null;

  const flightNumbers = legs.map((l) => l?.flightNumber).filter(Boolean);
  const flightNumber =
    flightNumbers.length > 0 ? flightNumbers.join(" · ") : null;

  const supplierStr =
    supplierCode != null ? String(supplierCode).trim() || null : null;

  const originCode = legEndpointCode(firstLeg, "dep");
  const destinationCode = legEndpointCode(lastLeg, "arr");
  const depDateDisplay = depDate ? formatDate(depDate) : "";

  if (process.env.NODE_ENV !== "test") {
    console.log("Flight card meta", {
      depDate,
      arrDate,
      flightCategory,
      airlineCode,
      supplierCode: supplierStr,
      flightNumber,
    });
  }

  return {
    departureTime,
    arrivalTime,
    timesLabel,
    flightCategory,
    airlineCode,
    supplierCode: supplierStr,
    flightNumber,
    duration: durationFormatted,
    depDate,
    arrDate,
    legCount,
    isDirect,
    originCode,
    destinationCode,
    depDateDisplay,
  };
}

/**
 * שורות תצוגה לכרטיס תוצאה: קטע לכל flight ב-linkedFlights (הלוך / חזור ב-RT).
 */
export function buildFlightCardLegRowsFromDeal(deal) {
  const segLegs = getFlightSegmentsWithLegs(deal);
  const supplierCode =
    deal?.supplierDetails?.supplierId ??
    deal?.supplierDetails?.SupplierId ??
    deal?.supplierdetails?.supplierId ??
    null;
  const supplierStr =
    supplierCode != null ? String(supplierCode).trim() || null : null;

  if (!segLegs.length) return [];

  return segLegs.map((sl, segIdx) => {
    const legs = sl.legs;
    const firstLeg = legs[0];
    const lastLeg = legs[legs.length - 1];
    const legCount = legs.length;
    const isDirect = legCount <= 1;
    let stopLabelHe = "ישירה";
    if (legCount === 2) stopLabelHe = "עצירה אחת";
    else if (legCount > 2) stopLabelHe = `${legCount - 1} עצירות`;

    const depDate = firstLeg?.depDate ?? null;
    const arrDate = lastLeg?.arrDate ?? null;
    const departureTime = depDate ? shortClockFromLdsDateTime(depDate) : "";
    const arrivalTime = arrDate ? shortClockFromLdsDateTime(arrDate) : "";

    let durationMinutes = 0;
    for (const L of legs) {
      const d = Number(L?.duration);
      if (Number.isFinite(d) && d > 0) durationMinutes += d;
    }
    const durationFormatted =
      durationMinutes > 0
        ? formatDurationMinutes(durationMinutes)
        : firstLeg?.duration != null
          ? formatDurationMinutes(Number(firstLeg.duration))
          : null;

    const flightCategory = firstLeg?.flightCategory ?? null;
    const airlineCode =
      firstLeg?.airlineId != null && String(firstLeg.airlineId).trim() !== ""
        ? String(firstLeg.airlineId).trim()
        : firstLeg?.airlineCode != null &&
            String(firstLeg.airlineCode).trim() !== ""
          ? String(firstLeg.airlineCode).trim()
          : null;

    const flightNumbers = legs.map((l) => l?.flightNumber).filter(Boolean);
    const flightNumber =
      flightNumbers.length > 0 ? flightNumbers.join(" · ") : null;

    const originCode = legEndpointCode(firstLeg, "dep");
    const destinationCode = legEndpointCode(lastLeg, "arr");
    const depDateDisplay = depDate ? formatDate(depDate) : "";

    const labelHe =
      segLegs.length === 1
        ? "הלוך"
        : segIdx === 0
          ? "הלוך"
          : segIdx === 1
            ? "חזור"
            : `קטע ${segIdx + 1}`;

    return {
      id: `seg-${segIdx}`,
      labelHe,
      departureTime,
      arrivalTime,
      originCode,
      destinationCode,
      depDate,
      arrDate,
      depDateDisplay,
      duration: durationFormatted,
      isDirect,
      stopLabelHe,
      flightCategory,
      airlineCode,
      flightNumber,
      supplierCode: supplierStr,
    };
  });
}

/** שורת כבודה קצרה לכרטיס תוצאה — מ-supplement ב-deal */
export function getFlightCardBaggageHint(deal) {
  if (!deal) return null;
  const rows = mapRawLdsSupplementsToMockupExtras(deal);
  if (rows.length === 0) return null;
  const first = rows[0];
  const cur = first.currency ? ` ${first.currency}` : "";
  if (rows.length === 1) {
    return `כבודה: ${first.titleLine} · ${first.fare}${cur}`;
  }
  return `כבודה זמינה · ${rows.length} אפשרויות`;
}

function normalizeSupplementsList(supp) {
  if (supp == null) return [];
  if (Array.isArray(supp)) return supp;
  return [supp];
}

/**
 * deal סינתטי לתצוגה/LDS init: שני OneWay כשני קטעי flight (הלוך + חזור).
 * inbound אמור להיות כבר origin/destination הפוכים מתוצאות החיפוש המקבילי.
 */
export function buildSyntheticRoundTripDealFromTwoOneWays(outDeal, inDeal) {
  if (!outDeal || !inDeal) return null;
  const outTf = Number(outDeal?.dealFare?.totalFare);
  const inTf = Number(inDeal?.dealFare?.totalFare);
  const totalFare =
    (Number.isFinite(outTf) ? outTf : 0) + (Number.isFinite(inTf) ? inTf : 0);
  const outSegs = getFlightSegments(outDeal);
  const inSegs = getFlightSegments(inDeal);
  const flightNodes = [];
  if (outSegs[0]) flightNodes.push(outSegs[0]);
  if (inSegs[0]) flightNodes.push(inSegs[0]);
  const combinedSupp = [
    ...normalizeSupplementsList(outDeal.supplement),
    ...normalizeSupplementsList(inDeal.supplement),
  ];
  const idOut = outDeal?.dealId ?? outDeal?.ID ?? "out";
  const idIn = inDeal?.dealId ?? inDeal?.ID ?? "in";
  const outA = positiveFareNumber(outDeal?.dealFare?.adultFare);
  const inA = positiveFareNumber(inDeal?.dealFare?.adultFare);
  const dealFareBase = { ...(outDeal.dealFare || {}) };
  dealFareBase.totalFare = totalFare;
  if (outA != null && inA != null) {
    dealFareBase.adultFare = outA + inA;
  }
  const fareDetails = {
    ...(outDeal.fareDetails || inDeal.fareDetails || {}),
  };
  if (!fareDetails.commission) {
    fareDetails.commission =
      outDeal.fareDetails?.commission ||
      inDeal.fareDetails?.commission || {
        commissionType: "NET",
        commissionValue: 0,
      };
  }
  return {
    isSelfComposeCombined: true,
    selfComposeDirectionCount: 2,
    dealId: `SC-${idOut}-${idIn}`,
    ID: `SC-${idOut}-${idIn}`,
    dealType: outDeal.dealType || inDeal.dealType || "Flights",
    dealtype: outDeal.dealtype || inDeal.dealtype || outDeal.dealType || inDeal.dealType || "Flights",
    dealFare: dealFareBase,
    fareDetails,
    supplierDetails: outDeal.supplierDetails || inDeal.supplierDetails || null,
    linkedFlights:
      flightNodes.length > 0
        ? { flight: flightNodes }
        : outDeal.linkedFlights || inDeal.linkedFlights,
    supplement: combinedSupp.length ? combinedSupp : outDeal.supplement,
  };
}

/** הצעת mockup משולבת ל־selectedFlightOffer אחרי בחירת שני כיוונים */
export function buildCombinedSelfComposeFlightOffer(
  outDeal,
  inDeal,
  roomInfo,
  options = {}
) {
  const synthetic = buildSyntheticRoundTripDealFromTwoOneWays(outDeal, inDeal);
  if (!synthetic) return null;
  const offer = adaptDealToFlightOffer(synthetic, 0, roomInfo, {
    ourPrice: options?.ourPrice,
  });
  return {
    ...offer,
    isSelfComposeCombined: true,
    selfComposeLegs: { outboundRawDeal: outDeal, inboundRawDeal: inDeal },
    source: "selfCompose",
  };
}

/**
 * Skeleton של roomsAndPaxInfo ל-initLdsSupplements כשאין עדיין טופס נוסעים —
 * מבוסס על room_compositions מחיפוש (adults / kids / infants כמספרים).
 * @param {Array<{ adults?: number, kids?: number, infants?: number }>|undefined} rooms
 * @returns {{ adults: object[], kids: object[], infants?: object[], room: null }[]}
 */
/** ספירת נוסעים לטיסה (מבוגרים + ילדים + תינוקות) לפי room_compositions בחיפוש */
export function countFlightPassengersFromRoomInfo(roomInfo) {
  const fromSearch = countTotalPaxFromRoomInfo(roomInfo);
  if (fromSearch != null && fromSearch > 0) return fromSearch;
  const rooms = roomInfo?.rooms;
  if (Array.isArray(rooms) && rooms.length > 0) return 1;
  return 2;
}

/** ברירת מחדל לאזרחות בטופס נוסעי טיסה (ISO 3166-1 alpha-2) — ניתן להחליף ב-UI */
export const DEFAULT_FLIGHT_PASSENGER_NATIONALITY = "IL";

export function createEmptyFlightPassengerSlot(index) {
  return {
    slotIndex: index,
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    passportNumber: "",
    passportExpiry: "",
    nationality: DEFAULT_FLIGHT_PASSENGER_NATIONALITY,
    cell: "",
    email: "",
  };
}

/**
 * ממזג את טופס הנוסעים של המוקאב לתוך עותק של roomsAndPaxInfo (מבנה LDS),
 * ומוסיף `_mockPaxId` לכל נוסע לצורך התאמה ל־supplementPaxSelections.
 */
export function mergeBookingFlowPassengersIntoRoomsAndPaxInfo(
  roomsAndPaxInfo,
  _roomInfo,
  passengers
) {
  const rooms = JSON.parse(JSON.stringify(roomsAndPaxInfo || []));
  const paxArr = Array.isArray(passengers) ? passengers : [];
  let g = 0;

  for (let ri = 0; ri < rooms.length; ri++) {
    const room = rooms[ri];

    for (let j = 0; j < (room.adults || []).length; j++) {
      const p = paxArr[g++];
      const id = `r${ri}_adult_${j}`;
      if (p && room.adults[j]) {
        room.adults[j] = {
          ...room.adults[j],
          _mockPaxId: id,
          firstname: p.firstName ?? p.firstname ?? "",
          lastname: p.lastName ?? p.lastname ?? "",
          dateOfBirth: p.dateOfBirth ?? "",
          gender: p.gender ?? "",
          passportNumber: p.passportNumber ?? "",
          passportExpiry: p.passportExpiry ?? "",
          nationality: p.nationality ?? "",
          email: p.email ?? "",
          cell: p.cell ?? p.phone ?? "",
        };
      } else if (room.adults[j]) {
        room.adults[j]._mockPaxId = id;
      }
    }
    for (let j = 0; j < (room.kids || []).length; j++) {
      const p = paxArr[g++];
      const id = `r${ri}_child_${j}`;
      if (p && room.kids[j]) {
        room.kids[j] = {
          ...room.kids[j],
          _mockPaxId: id,
          firstname: p.firstName ?? p.firstname ?? "",
          lastname: p.lastName ?? p.lastname ?? "",
          dateOfBirth: p.dateOfBirth ?? "",
          gender: p.gender ?? "",
          passportNumber: p.passportNumber ?? "",
          passportExpiry: p.passportExpiry ?? "",
          nationality: p.nationality ?? "",
          email: p.email ?? "",
          cell: p.cell ?? p.phone ?? "",
        };
      } else if (room.kids[j]) {
        room.kids[j]._mockPaxId = id;
      }
    }
    for (let j = 0; j < (room.infants || []).length; j++) {
      const p = paxArr[g++];
      const id = `r${ri}_infant_${j}`;
      if (p && room.infants[j]) {
        room.infants[j] = {
          ...room.infants[j],
          _mockPaxId: id,
          firstname: p.firstName ?? p.firstname ?? "",
          lastname: p.lastName ?? p.lastname ?? "",
          dateOfBirth: p.dateOfBirth ?? "",
          gender: p.gender ?? "",
          passportNumber: p.passportNumber ?? "",
          passportExpiry: p.passportExpiry ?? "",
          nationality: p.nationality ?? "",
          email: p.email ?? "",
          cell: p.cell ?? p.phone ?? "",
        };
      } else if (room.infants[j]) {
        room.infants[j]._mockPaxId = id;
      }
    }
  }
  return rooms;
}

export function buildPaxSlotsForLdsSupplementsFromSearchRooms(rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  const rows =
    list.length > 0
      ? list
      : [{ adults: 2, kids: 0, infants: 0 }];
  return rows.map((room) => {
    const adults = Math.max(0, Math.floor(Number(room?.adults) || 0));
    const kids = Math.max(0, Math.floor(Number(room?.kids) || 0));
    const infants = Math.max(0, Math.floor(Number(room?.infants) || 0));
    return {
      adults: Array.from({ length: adults }, () => ({
        firstname: null,
        lastname: null,
      })),
      kids: Array.from({ length: kids }, () => ({
        firstname: null,
        lastname: null,
      })),
      infants: Array.from({ length: infants }, () => ({
        firstname: null,
        lastname: null,
      })),
      room: null,
    };
  });
}

/**
 * שורות טקסט לשלב פירוט טיסה — לפי קטעי flight (הלוך/חזור), לא לפי אינדקס גלובלי בלבד.
 * @param {object} offer - כמו מ-adaptDealToFlightOffer
 */
export function buildFlightDetailBullets(offer) {
  const deal = offer?.rawDeal;
  if (!deal) {
    return [
      offer?.route || "—",
      offer?.pricePerPersonDisplayLabel ||
        offer?.priceDisplayLabel ||
        (offer?.priceUsd != null ? `${offer.priceUsd}` : "—"),
    ];
  }
  const segLegs = getFlightSegmentsWithLegs(deal);
  if (segLegs.length === 0) {
    return [
      buildRouteLabel(deal),
      offer?.pricePerPersonDisplayLabel || offer?.priceDisplayLabel || "",
    ].filter(Boolean);
  }

  const lines = [];
  segLegs.forEach((sl, segIdx) => {
    const multiSeg = segLegs.length > 1;
    const segDir =
      multiSeg && segIdx === 0
        ? "הלוך"
        : multiSeg
          ? "חזור"
          : sl.legs.length > 1
            ? null
            : "טיסה";

    sl.legs.forEach((leg, legIdx) => {
      let dirLabel;
      if (segDir) {
        dirLabel =
          sl.legs.length > 1 ? `${segDir} · קטע ${legIdx + 1}` : segDir;
      } else {
        dirLabel = `קטע ${legIdx + 1}`;
      }
      lines.push(formatLegBulletLine(leg, dirLabel));
    });
  });
  return lines;
}

/** שורות תיאור כבודה מסומנת מ־ldsSupplement (מוקאב) */
export function buildCheckedBaggageSummaryLines(ldsSupplement, currency) {
  const cur = currency ? String(currency).trim() : "";
  if (!ldsSupplement || typeof ldsSupplement !== "object") {
    return ["כבודה: לא נבחרה תוספת"];
  }
  const lines = [];
  for (const key of Object.keys(ldsSupplement)) {
    const e = ldsSupplement[key];
    if (!e?.checked) continue;
    const fare = e?.orig?.supp?.fare;
    const desc = e?.description || e?.type || "כבודה";
    const wt = e?.weightText ? ` ${e.weightText}` : "";
    const fareStr =
      fare != null && Number.isFinite(Number(fare))
        ? `${fare}${cur ? ` ${cur}` : ""}`
        : "—";
    lines.push(`${desc}${wt}: ${fareStr}`);
  }
  if (lines.length === 0) return ["כבודה: לא נבחרה תוספת"];
  return lines;
}

export function buildReviewSummaryLines(offer, passengers) {
  const lines = [];
  if (offer?.route) lines.push(`מסלול: ${offer.route}`);
  const perPerson =
    offer?.pricePerPersonDisplayLabel || offer?.priceDisplayLabel;
  if (perPerson && perPerson !== "—") {
    lines.push(`מחיר (ספק): ${perPerson}`);
  }
  if (offer?.resultsTotalFareSubline) {
    lines.push(offer.resultsTotalFareSubline);
  }
  if (offer?.airlineSummary && offer.airlineSummary !== "—")
    lines.push(`חברות: ${offer.airlineSummary}`);
  const n = Array.isArray(passengers) ? passengers.length : 0;
  lines.push(`נוסעים: ${n}`);
  if (n > 0) {
    passengers.forEach((p, i) => {
      const fromParts =
        p?.firstName || p?.lastName
          ? `${p?.firstName ?? ""} ${p?.lastName ?? ""}`.trim()
          : "";
      const name = fromParts || p?.fullName || p?.name || `נוסע ${i + 1}`;
      lines.push(`  · ${name}`);
    });
  }
  return lines;
}

export {
  analyzeFlightBaggageSupplementsForFlow,
  getFlightBaggageIncludedBadge,
  hasSelectableBaggageExtras,
  isBaggageIncludedForFlight,
  shouldSkipBaggageStep,
} from "./flightBaggageStepPolicy";
